/**
 * Maria01 is a Helsinki startup campus address label; normalize it to Helsinki for facets, stats, and UX.
 */

/** City token only: "Maria01", "maria 01", "Maria_01", etc. */
const MARIA01_CITY_RE = /^maria[\s_-]*0[\s_-]*1$/i;

export interface JobWithLocaleLocationArrays {
  location_en?: string[];
  location_fi?: string[];
  location_sv?: string[];
}

export const MARIA01_HELSINKI_BY_LOCALE = {
  en: 'Helsinki, Finland',
  fi: 'Helsinki, Suomi',
  sv: 'Helsingfors, Finland',
} as const;

export const MARIA01_HELSINKI_CANONICAL = MARIA01_HELSINKI_BY_LOCALE.en;

/**
 * True if the whole string is Maria01, or the city segment before the first comma is Maria01.
 */
export function locationStringRepresentsMaria01(loc: string): boolean {
  if (typeof loc !== 'string') return false;
  const trimmed = loc.trim();
  if (!trimmed) return false;
  if (MARIA01_CITY_RE.test(trimmed)) return true;
  const comma = trimmed.indexOf(',');
  if (comma === -1) return false;
  const city = trimmed.slice(0, comma).trim();
  return MARIA01_CITY_RE.test(city);
}

/** Always "Helsinki, Finland" — use before {@link normalizeLocationCountryNames} / Finnish locality passes. */
export function replaceMaria01WithCanonicalHelsinki(loc: string): string {
  if (!locationStringRepresentsMaria01(loc)) return loc;
  return MARIA01_HELSINKI_CANONICAL;
}

export function replaceMaria01ForLocale(loc: string, locale: 'en' | 'fi' | 'sv'): string {
  if (!locationStringRepresentsMaria01(loc)) return loc;
  return MARIA01_HELSINKI_BY_LOCALE[locale];
}

/** Ingest pipeline: rewrite all locale location arrays to canonical English; downstream steps localize city/country. */
export function replaceMaria01CanonicalInJobLocaleLocationArrays(job: JobWithLocaleLocationArrays): void {
  const keys: Array<'location_en' | 'location_fi' | 'location_sv'> = ['location_en', 'location_fi', 'location_sv'];
  for (const key of keys) {
    const arr = job[key];
    if (!Array.isArray(arr)) continue;
    job[key] = arr.map((entry) =>
      typeof entry === 'string' ? replaceMaria01WithCanonicalHelsinki(entry) : entry,
    );
  }
}

/** Job detail / API responses: each array gets the correct locale label. */
export function replaceMaria01LocalizedInJobLocaleLocationArrays(job: JobWithLocaleLocationArrays): void {
  const patch = (key: 'location_en' | 'location_fi' | 'location_sv', locale: 'en' | 'fi' | 'sv') => {
    const arr = job[key];
    if (!Array.isArray(arr)) return;
    job[key] = arr.map((entry) =>
      typeof entry === 'string' ? replaceMaria01ForLocale(entry, locale) : entry,
    );
  };
  patch('location_en', 'en');
  patch('location_fi', 'fi');
  patch('location_sv', 'sv');
}
